<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-3 sm:p-4">
    <div class="bg-white rounded-3xl max-w-xl w-full shadow-2xl relative overflow-hidden animate-fade-in my-auto max-h-[88vh] flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100 flex-shrink-0 bg-white">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center text-lg">
            <i class="fa fa-folder-open" />
          </div>
          <div>
            <h2 class="text-base font-bold text-gray-900">
              Expediente: {{ employee?.name }}
            </h2>
            <p class="text-xs text-gray-500">Documentos digitales adjuntos (RIF, certificados, etc.)</p>
          </div>
        </div>
        <button 
          type="button"
          class="text-gray-400 hover:text-gray-600 text-lg p-1 transition-colors cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <!-- Upload Form Box -->
        <form class="bg-amber-50/50 p-4 rounded-2xl border border-amber-200/80 space-y-3" @submit.prevent="submitUpload">
          <h3 class="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
            <i class="fa fa-cloud-upload-alt text-amber-600" />
            Subir Nuevo Documento
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Nombre del Documento *</label>
              <input
                v-model="newDocName"
                type="text"
                required
                placeholder="Ej. RIF, Certificado Sanitario, etc."
                class="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs focus:ring-amber-500 focus:border-amber-500 font-medium"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-gray-700 mb-1">Archivo (PDF, Imagen, Doc) *</label>
              <input
                ref="fileInputRef"
                type="file"
                required
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                class="w-full text-xs text-gray-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 cursor-pointer"
                @change="onFileChange"
              />
            </div>
          </div>

          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="uploading || !newDocName || !newDocFile"
              class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition-all disabled:opacity-50 cursor-pointer active:scale-95"
            >
              <i v-if="uploading" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-upload" />
              <span>{{ uploading ? 'Subiendo...' : 'Adjuntar Archivo' }}</span>
            </button>
          </div>
        </form>

        <!-- List of uploaded documents -->
        <div>
          <h4 class="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <i class="fa fa-paperclip text-gray-400" />
            Archivos Registrados ({{ documentsList.length }})
          </h4>

          <div v-if="loading" class="py-8 text-center text-gray-400 text-xs flex items-center justify-center gap-2">
            <i class="fa fa-spinner fa-spin text-base text-amber-500" />
            <span>Cargando expediente...</span>
          </div>

          <div v-else-if="documentsList.length === 0" class="p-6 text-center border-2 border-dashed border-gray-200 rounded-2xl text-gray-400">
            <i class="fa fa-folder-open text-2xl mb-1 text-gray-300" />
            <p class="text-xs font-medium">No hay documentos registrados para este empleado aún.</p>
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="doc in documentsList"
              :key="doc.id"
              class="p-3 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-3 hover:border-amber-300 transition-all shadow-2xs"
            >
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center text-sm shrink-0">
                  <i v-if="doc.file_type === 'pdf'" class="fa fa-file-pdf text-red-500" />
                  <i v-else-if="['jpg', 'jpeg', 'png'].includes(doc.file_type)" class="fa fa-file-image text-blue-500" />
                  <i v-else class="fa fa-file text-amber-600" />
                </div>
                <div class="min-w-0">
                  <div class="text-xs font-bold text-gray-900 truncate">{{ doc.name }}</div>
                  <div class="text-[11px] text-gray-400 truncate flex items-center gap-1.5">
                    <span>{{ doc.file_name }}</span>
                    <span v-if="doc.file_size">• {{ formatBytes(doc.file_size) }}</span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-1.5 shrink-0">
                <a
                  :href="doc.file_path"
                  target="_blank"
                  class="p-1.5 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors"
                  title="Ver / Descargar archivo"
                >
                  <i class="fa fa-external-link-alt" />
                  <span class="hidden sm:inline">Ver</span>
                </a>
                <button
                  type="button"
                  class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg text-xs transition-colors cursor-pointer"
                  title="Eliminar documento"
                  @click="handleDelete(doc)"
                >
                  <i class="fa fa-trash-alt" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky Footer -->
      <div class="px-6 py-3 bg-gray-50 border-t border-gray-100 shrink-0 flex items-center justify-end rounded-b-3xl">
        <button
          type="button"
          class="px-4 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 font-medium text-xs hover:bg-gray-100 transition-colors cursor-pointer"
          @click="$emit('close')"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2'

export default {
  name: 'DocumentsModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: null,
    },
  },

  emits: ['close', 'document-uploaded', 'document-deleted'],

  data() {
    return {
      loading: false,
      uploading: false,
      documentsList: [],
      newDocName: '',
      newDocFile: null,
    }
  },

  watch: {
    isOpen(val) {
      if (val && this.employee) {
        this.newDocName = ''
        this.newDocFile = null
        if (this.$refs.fileInputRef) {
          this.$refs.fileInputRef.value = ''
        }
        this.loadDocuments()
      }
    },
  },

  methods: {
    loadDocuments() {
      if (!this.employee) return
      this.loading = true
      fetch(`/paysheet/employees/${this.employee.id}/documents`, {
        headers: { 'Accept': 'application/json' },
      })
        .then(r => r.json())
        .then(data => {
          this.documentsList = data.documents || []
        })
        .catch(err => {
          console.error('Error cargando documentos:', err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    onFileChange(e) {
      const files = e.target.files
      if (files && files[0]) {
        this.newDocFile = files[0]
        if (!this.newDocName) {
          const rawName = files[0].name.replace(/\.[^/.]+$/, '')
          this.newDocName = rawName
        }
      }
    },

    async submitUpload() {
      if (!this.newDocFile || !this.employee) return
      this.uploading = true

      const formData = new FormData()
      formData.append('name', this.newDocName)
      formData.append('file', this.newDocFile)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        formData.append('_token', csrfToken)
      }

      try {
        const res = await fetch(`/paysheet/employees/${this.employee.id}/documents`, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
            ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
          },
        })
        const data = await res.json()
        if (res.ok && data.success) {
          this.newDocName = ''
          this.newDocFile = null
          if (this.$refs.fileInputRef) {
            this.$refs.fileInputRef.value = ''
          }
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Documento subido correctamente',
            showConfirmButton: false,
            timer: 2000,
          })
          this.loadDocuments()
          this.$emit('document-uploaded', this.employee)
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Error al subir el archivo' })
        }
      } catch (err) {
        console.error('Error al subir:', err)
        Swal.fire({ icon: 'error', title: 'Error', text: 'Error de conexión al subir el documento' })
      } finally {
        this.uploading = false
      }
    },

    handleDelete(doc) {
      Swal.fire({
        title: '¿Eliminar documento?',
        text: `Se borrará "${doc.name}" permanentemente.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
      }).then(async result => {
        if (result.isConfirmed) {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          try {
            const res = await fetch(`/paysheet/documents/${doc.id}`, {
              method: 'DELETE',
              headers: {
                'Accept': 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
              },
            })
            if (res.ok) {
              this.loadDocuments()
              this.$emit('document-deleted', this.employee)
              Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Documento eliminado',
                showConfirmButton: false,
                timer: 1500,
              })
            } else {
              Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo eliminar el documento' })
            }
          } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo eliminar el documento' })
          }
        }
      })
    },

    formatBytes(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
  },
}
</script>
